import { type ReactNode, useCallback, useMemo, useRef } from "react";
import { useElements } from "@stripe/react-stripe-js";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { billingInfoApi } from "@/react/api/paywalls/communityMembers/billingInfoApi";
import { Form } from "@/react/components/shared/uikit/Form";
import type { OnSubmitFunction } from "@/react/components/shared/uikit/Form/Form";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import type { ApiError } from "@/react/config/CustomErrors";
import type { CommunityMemberBillingInfo } from "@/react/types";
import { getTaxIdLabelByType } from "@/react/utils/paywalls/taxId/helpers";
import type { BillingInfoContextProps } from "./BillingInfoContextProps";
import { BillingInfoContextProvider } from "./BillingInfoContextProvider";
import type { BillingInfoFormValues } from "./BillingInfoFormValues";
import { localeRoot } from "./constants";

const submittingMessage = t([localeRoot, "submitting"]);
const successMessage = t([localeRoot, "success"]);

export interface BillingInfoFormWrapperProps extends BillingInfoContextProps {
  children: ReactNode;
}

export const BillingInfoFormWrapper = ({
  children,
  communityMember,
  communityMemberBillingInfo,
  onSuccess,
  ...props
}: BillingInfoFormWrapperProps) => {
  const {
    show: showToast,
    remove: removeToast,
    success: showSuccess,
    error: showError,
  } = useToast();

  const elements = useElements();

  const defaultValues = useMemo<BillingInfoFormValues>(
    () => ({
      address_city: communityMemberBillingInfo?.address_city,
      address_complete: communityMemberBillingInfo?.address_complete,
      address_country: communityMemberBillingInfo?.address_country,
      address_line1: communityMemberBillingInfo?.address_line1,
      address_line2: communityMemberBillingInfo?.address_line2,
      address_postal_code: communityMemberBillingInfo?.address_postal_code,
      address_state: communityMemberBillingInfo?.address_state,
      business_name: communityMemberBillingInfo?.business_name,
      tax_id_type_label: getTaxIdLabelByType(
        communityMemberBillingInfo?.tax_id_type,
      ),
      tax_id_type: communityMemberBillingInfo?.tax_id_type,
      tax_id_value: communityMemberBillingInfo?.tax_id_value,
    }),
    [communityMemberBillingInfo],
  );

  const createOrUpdateBillingInfo = useCallback(
    async (data: BillingInfoFormValues) => {
      if (communityMemberBillingInfo.id) {
        return billingInfoApi.update(communityMember.id, data);
      }
      return billingInfoApi.create(communityMember.id, data);
    },
    [communityMember, communityMemberBillingInfo],
  );

  const billingInfoMutationToastId = useRef<string | null>(null);
  const billingInfoMutation = useMutation<
    CommunityMemberBillingInfo,
    ApiError,
    BillingInfoFormValues
  >(createOrUpdateBillingInfo, {
    onMutate: () => {
      billingInfoMutationToastId.current = showToast(submittingMessage, {
        shouldAutohide: false,
        shouldUseIndefiniteIcon: true,
      });
    },
    onSuccess: () => {
      showSuccess(successMessage, {
        duration: "short",
        shouldAutohide: true,
        shouldUseProgress: false,
      });
      void onSuccess();
    },
    onError: error => {
      showError(error.message, {
        duration: "short",
        shouldAutohide: true,
        shouldUseProgress: false,
      });
    },
    onSettled: () => {
      if (billingInfoMutationToastId.current) {
        removeToast(billingInfoMutationToastId.current);
      }
    },
  });

  const onSubmit = useCallback<OnSubmitFunction<BillingInfoFormValues>>(
    async (data, { reset }) => {
      const result = await elements?.submit();
      if (result?.error) {
        // No need to show error, it's already shown by the Elements component
        return;
      }
      await billingInfoMutation.mutateAsync(data, {
        onSuccess: updatedBillingInfo => {
          reset({
            address_city: updatedBillingInfo?.address_city,
            address_complete: updatedBillingInfo?.address_complete,
            address_country: updatedBillingInfo?.address_country,
            address_line1: updatedBillingInfo?.address_line1,
            address_line2: updatedBillingInfo?.address_line2,
            address_postal_code: updatedBillingInfo?.address_postal_code,
            address_state: updatedBillingInfo?.address_state,
            business_name: updatedBillingInfo?.business_name,
            tax_id_type_label: getTaxIdLabelByType(
              updatedBillingInfo?.tax_id_type,
            ),
            tax_id_type: updatedBillingInfo?.tax_id_type,
            tax_id_value: updatedBillingInfo?.tax_id_value,
          });
        },
      });
    },
    [elements, billingInfoMutation],
  );

  return (
    <Form defaultValues={defaultValues} onSubmit={onSubmit}>
      <BillingInfoContextProvider
        {...props}
        communityMember={communityMember}
        communityMemberBillingInfo={communityMemberBillingInfo}
        billingInfoMutation={billingInfoMutation}
      >
        {children}
      </BillingInfoContextProvider>
    </Form>
  );
};
